import { X, Share2 } from "lucide-react";
import { resolveCoverUrl } from "@/lib/api";
import { pickSpineColor } from "@/lib/constants";
import { toast } from "sonner";

export default function BookDetailModal({ book, onClose }) {
  if (!book) return null;
  const color = pickSpineColor(book.title + book.author_name);
  const coverUrl = resolveCoverUrl(book.cover_image_url);

  const handleShare = async () => {
    const shareData = {
      title: book.title,
      text: `${book.title} by ${book.author_name} — on The Bookshelf`,
      url: book.purchase_url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(book.purchase_url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 animate-fade-up"
      style={{ background: "rgba(14, 35, 46, 0.65)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-detail-title"
      data-testid="book-detail-modal"
    >
      <div
        className="relative bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-[#CFD6DC]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 border border-[#CFD6DC] flex items-center justify-center text-[#0E232E] hover:bg-[#F0F3F5] transition-colors shadow-sm"
          data-testid="book-detail-close"
        >
          <X size={18} />
        </button>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-0">
          {/* Cover side */}
          <div className="relative bg-gradient-to-br from-[#F8F6F0] to-[#ECEBE4] flex items-center justify-center p-8 md:p-12 min-h-[280px]">
            <div
              className="absolute inset-0 opacity-50"
              style={{
                background: `radial-gradient(circle at 30% 30%, ${color}33 0%, transparent 65%)`,
              }}
              aria-hidden="true"
            />
            <div
              className="relative w-full max-w-[260px] aspect-[2/3] rounded-md shadow-[0_25px_45px_-15px_rgba(14,35,46,0.45)] overflow-hidden border-l-[5px] border-black/30 border-r border-black/15"
              style={{
                backgroundColor: color,
                backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="book-detail-cover"
            >
              {!coverUrl && (
                <div className="w-full h-full flex items-center justify-center p-3">
                  <span className="font-display text-lg font-bold text-white text-center leading-tight">
                    {book.title}
                  </span>
                </div>
              )}
            </div>
            {book.is_new && (
              <span className="absolute top-4 left-4 bg-[#2B328C] text-white text-[10px] uppercase font-bold px-2.5 py-1 rounded shadow-md tracking-wider border border-[#1F2470]">
                New this month
              </span>
            )}
          </div>

          {/* Detail side */}
          <div className="p-6 md:p-8 flex flex-col">
            <span className="self-start text-[10px] font-bold uppercase tracking-wider bg-[#7EC8E3]/20 text-[#0E232E] px-2 py-1 rounded">
              {book.age_group_label}
            </span>
            <h2
              id="book-detail-title"
              className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-[#0E232E] leading-tight tracking-tight"
            >
              {book.title}
            </h2>
            <p className="mt-2 text-sm text-[#2F2E2E] font-medium">
              by <span className="text-[#0E232E] font-bold">{book.author_name}</span>
            </p>

            <div className="mt-5 text-[15px] text-[#2F2E2E] leading-relaxed whitespace-pre-line">
              {book.description}
            </div>

            <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
              <a
                href={book.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-teal px-6 py-3 rounded-full text-sm uppercase tracking-wider"
                data-testid="book-detail-buy"
              >
                Get this book
              </a>
              <button
                type="button"
                onClick={handleShare}
                className="px-4 py-3 rounded-full border border-[#CFD6DC] text-[#0E232E] font-bold text-sm flex items-center gap-2 hover:bg-[#F0F3F5] transition-colors"
                aria-label="Share this book"
                data-testid="book-detail-share"
              >
                <Share2 size={14} /> Share
              </button>
              {book.author_link && (
                <a
                  href={book.author_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#2B328C] underline font-semibold"
                >
                  Author's website
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
